// src/features/products/ProductsAdmin.jsx
import React, { useEffect, useState } from "react";
import { apiGet, apiDel, apiPostForm, apiPutForm, API_BASE } from "../../admin/api";
import Editor from "../../admin/components/Editor";
import "./products-admin.css";

export default function ProductsAdmin() {
  const [tab, setTab] = useState("categories"); // "categories" | "items"
  return (
    <div className="products-admin">
      <div className="products-toolbar">
        <h2 className="products-toolbar__title">Products</h2>
        <div className="products-tabs">
          <button
            className={`products-tab ${tab === "categories" ? "is-active" : ""}`}
            onClick={() => setTab("categories")}
          >
            Categories
          </button>
          <button
            className={`products-tab ${tab === "items" ? "is-active" : ""}`}
            onClick={() => setTab("items")}
          >
            Items
          </button>
        </div>
      </div>

      {tab === "categories" ? <ProductCategories /> : <ProductItems />}
    </div>
  );
}

/* =========================
   Categories
========================= */
function ProductCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ id: null, name: "", description: "" });
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/products/categories");
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ id: null, name: "", description: "" }); setShowForm(true); };
  const startEdit = (r) => { setForm({ id: r.id, name: r.name, description: r.description || "" }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const body = JSON.stringify({ name: form.name, description: form.description });
    const headers = { "Content-Type": "application/json" };
    if (form.id) {
      await fetch(`${API_BASE}/api/products/categories/${form.id}`, {
        method: "PUT", headers, credentials: "include", body
      }).then(r=>r.json()).then(d=>{ if(d?.error) throw new Error(d.error); });
    } else {
      await fetch(`${API_BASE}/api/products/categories`, {
        method: "POST", headers, credentials: "include", body
      }).then(r=>r.json()).then(d=>{ if(d?.error) throw new Error(d.error); });
    }
    setShowForm(false);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await apiDel(`/api/products/categories/${id}`);
    await load();
  };

  return (
    <>
      <div className="products-toolbar__right">
        <button className="products-btn products-btn--cta" onClick={startNew}>+ New Category</button>
      </div>

      {loading ? (
        <div className="products-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="products-empty-block">
          <div className="products-empty-block__icon">🗂️</div>
          <div className="products-empty-block__title">No categories</div>
          <div className="products-empty-block__desc">Create your first product category.</div>
          <button className="products-btn products-btn--cta" onClick={startNew}>+ Create category</button>
        </div>
      ) : (
        <div className="products-grid">
          {rows.map(r => (
            <article key={r.id} className="products-card">
              <div className="products-card__body">
                <h3 className="products-card__title">{r.name}</h3>
                {r.description && <p className="products-card__excerpt">{r.description}</p>}
                <div className="products-card__actions">
                  <button className="products-btn products-btn--ghost" onClick={() => startEdit(r)}>Edit</button>
                  <button className="products-btn products-btn--danger" onClick={() => remove(r.id)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showForm && (
        <div className="products-modal" role="dialog" aria-modal="true">
          <div className="products-modal__backdrop" onClick={() => setShowForm(false)} />
          <div className="products-modal__card" role="document">
            <div className="products-modal__head">
              <h3 className="products-modal__title">{form.id ? "Edit Category" : "New Category"}</h3>
              <button className="products-btn products-btn--close" onClick={() => setShowForm(false)}>×</button>
            </div>

            <form onSubmit={save} className="products-form">
              <label className="products-field">
                <span className="products-field__label">Name</span>
                <input
                  className="products-field__input"
                  value={form.name}
                  onChange={e=>setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>

              <label className="products-field">
                <span className="products-field__label">Description</span>
                <input
                  className="products-field__input"
                  value={form.description}
                  onChange={e=>setForm({ ...form, description: e.target.value })}
                />
              </label>

              <div className="products-modal__actions">
                <button type="button" className="products-btn products-btn--sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="products-btn products-btn--primary" type="submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================
   Items
========================= */
function ProductItems() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCatSlug, setActiveCatSlug] = useState("");  // for API calls by slug
  const [activeCatId, setActiveCatId] = useState(null);     // for UI highlight fallback
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: null, title: "", excerpt: "", status: "published", content_html: "", category_id: ""
  });
  const [cover, setCover] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const catList = await apiGet("/api/products/categories");
      const list = Array.isArray(catList) ? catList : [];
      setCats(list);

      // Pick the FIRST category and make it active immediately
      const first = list[0];
      if (first) {
        setActiveCatId(first.id);

        if (first.slug) {
          setActiveCatSlug(first.slug);
          await viewByCategory(first.slug, { setLoadingAfter: false });
        } else {
          // No slug → we still mark the first pill active; items remain empty until user picks
          setItems([]);
        }
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    // preselect the active category id
    const activeId = activeCatId || cats[0]?.id || "";
    setForm({
      id: null, title: "", excerpt: "", status: "published", content_html: "",
      category_id: activeId
    });
    setCover(null);
    setShowForm(true);
  };

  const startEditFromFetch = async (slug) => {
    const row = await apiGet(`/api/products/${slug}`);
    setForm({
      id: row.id,
      title: row.title,
      excerpt: row.excerpt || "",
      status: row.status || "published",
      content_html: row.content_html || "",
      category_id: row.category_id || row.ProductCategory?.id || ""
    });
    setCover(null);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title,
      excerpt: form.excerpt,
      status: form.status,
      content_html: form.content_html,
      category_id: form.category_id
    };
    if (cover) payload.cover_image = cover;

    if (form.id) {
      await apiPutForm(`/api/products/${form.id}`, payload);
    } else {
      await apiPostForm(`/api/products`, payload);
    }
    setShowForm(false);

    // refresh current list if a slug is active
    if (activeCatSlug) {
      await viewByCategory(activeCatSlug);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await apiDel(`/api/products/${id}`);
    if (activeCatSlug) await viewByCategory(activeCatSlug);
  };

  // Fetch items by category slug
  const viewByCategory = async (slug, opts = {}) => {
    const { setLoadingAfter = true } = opts;
    if (setLoadingAfter) setLoading(true);
    try {
      const data = await apiGet(`/api/products/category/${slug}/items?status=`);
      setItems(data.items || []);
      setActiveCatSlug(slug);
      // sync activeCatId with slug (for UI highlight)
      const match = cats.find(c => c.slug === slug);
      if (match) setActiveCatId(match.id);
    } finally {
      if (setLoadingAfter) setLoading(false);
    }
  };

  return (
    <>
      <div className="products-row">
        <div className="products-pillbar">
          {cats.map((c) => {
            const isActive = activeCatId === c.id; // highlight even if no slug
            const hasSlug = Boolean(c.slug);
            return (
              <button
                key={c.id}
                className={`products-pill${isActive ? " is-active" : ""}`}
                onClick={() => {
                  setActiveCatId(c.id);
                  if (hasSlug) viewByCategory(c.slug);
                  else setItems([]); // no slug → just highlight
                }}
                disabled={!hasSlug && false} // allow focus/highlight even without slug
                title={hasSlug ? c.name : "No slug; list won’t load"}
              >
                {c.name}
              </button>
            );
          })}
        </div>
        <button className="products-btn products-btn--cta" onClick={startNew}>+ New Product</button>
      </div>

      {loading ? (
        <div className="products-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="products-empty-block">
          <div className="products-empty-block__icon">📦</div>
          <div className="products-empty-block__title">No products to show</div>
          <div className="products-empty-block__desc">
            {activeCatSlug ? "This category has no items yet." : "Pick a category above or create a new product."}
          </div>
        </div>
      ) : (
        <div className="products-grid">
          {items.map((r) => {
            const coverUrl = r.cover_image ? `${API_BASE}${r.cover_image}` : null;
            const catName = cats.find((c) => c.id === r.category_id)?.name || "-";
            return (
              <article key={r.id} className="products-card">
                <div className="products-card__thumb">
                  {coverUrl ? (
                    <img src={coverUrl} alt={r.title} loading="lazy" />
                  ) : (
                    <div className="products-card__thumb--empty">No Image</div>
                  )}
                </div>
                <div className="products-card__body">
                  <div className="products-card__tags">
                    <span className="products-chip">{catName}</span>
                    <span className={`products-chip ${r.status === "published" ? "" : "products-chip--muted"}`}>
                      {r.status}
                    </span>
                  </div>
                  <h3 className="products-card__title">{r.title}</h3>
                  {r.excerpt && <p className="products-card__excerpt">{r.excerpt}</p>}
                  <div className="products-card__actions">
                    <button className="products-btn products-btn--ghost" onClick={() => startEditFromFetch(r.slug)}>Edit</button>
                    <button className="products-btn products-btn--danger" onClick={() => remove(r.id)}>Delete</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="products-modal" role="dialog" aria-modal="true">
          <div className="products-modal__backdrop" onClick={() => setShowForm(false)} />
          <div className="products-modal__card" role="document">
            <div className="products-modal__head">
              <h3 className="products-modal__title">{form.id ? "Edit Product" : "New Product"}</h3>
              <button className="products-btn products-btn--close" onClick={() => setShowForm(false)}>×</button>
            </div>

            <form onSubmit={save} className="products-form">
              <div className="products-row">
                <label className="products-field products-grow">
                  <span className="products-field__label">Title</span>
                  <input
                    className="products-field__input"
                    value={form.title}
                    onChange={e=>setForm({ ...form, title: e.target.value })}
                    required
                  />
                </label>

                <label className="products-field products-grow">
                  <span className="products-field__label">Category</span>
                  <select
                    className="products-field__input"
                    value={form.category_id}
                    onChange={e=>setForm({ ...form, category_id: e.target.value })}
                    required
                  >
                    <option value="">-- choose --</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
              </div>

              <label className="products-field">
                <span className="products-field__label">Excerpt</span>
                <input
                  className="products-field__input"
                  value={form.excerpt}
                  onChange={e=>setForm({ ...form, excerpt: e.target.value })}
                />
              </label>

              <div className="products-row">
                <label className="products-field products-grow">
                  <span className="products-field__label">Status</span>
                  <select
                    className="products-field__input"
                    value={form.status}
                    onChange={e=>setForm({ ...form, status: e.target.value })}
                  >
                    <option value="published">published</option>
                    <option value="draft">draft</option>
                  </select>
                </label>

                <label className="products-field products-grow">
                  <span className="products-field__label">Cover Image</span>
                  <input
                    className="products-field__input"
                    type="file"
                    accept="image/*"
                    onChange={e=>setCover(e.target.files?.[0]||null)}
                  />
                </label>
              </div>

              <label className="products-field">
                <span className="products-field__label">Content (Jodit HTML)</span>
                <div className="products-editor">
                  <Editor
                    value={form.content_html}
                    onChange={(html)=>setForm({ ...form, content_html: html })}
                  />
                </div>
              </label>

              <div className="products-modal__actions">
                <button type="button" className="products-btn products-btn--sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="products-btn products-btn--primary" type="submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

